import { useCallback, useMemo, useState } from "react";
import { FormikProps } from "formik";
import _ from "lodash";

import { AddIcon, EditIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";

import Autocomplete, {
  AutocompletePaperComponent,
  createFilterOptions,
} from "components/Autocomplete";
import { DialogTitle } from "components/Dialog";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";

import {
  calculateTransactionStatusOptions,
  TRANSACTION_TYPE_OPTIONS,
} from "pages/Deal/FinancingEvents/constants";
import TransactionSelectField from "pages/Deal/FinancingEvents/TransactionFields/TransactionSelectField";
import TransactionTextField from "pages/Deal/FinancingEvents/TransactionFields/TransactionTextField";

import {
  EPayableBackTransactionStatus,
  ETransactionStatus,
  ETransactionType,
} from "entities/Deal/constants";
import {
  IFinancingProgram,
  ITransactionValuesForm,
  useFinancingPrograms,
} from "entities/Deal/sdk";

import { checkAndUpdateStatusOnTypeChange } from "./utils";

const filter = createFilterOptions<IFinancingProgram>();

export interface ITransactionGeneralInformationForm {
  formik: FormikProps<ITransactionValuesForm>;
  lastUpdatedInformation: string | null;
  transactionIdentifier?: string;
  previousTransactionType?: ETransactionType | null;
}

const TransactionGeneralInformationForm = ({
  formik,
  transactionIdentifier,
  previousTransactionType,
  lastUpdatedInformation,
}: ITransactionGeneralInformationForm) => {
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const { data: financingPrograms } = useFinancingPrograms();

  const transactionStatusOptions = useCallback(
    ({ transactionType }: { transactionType: ETransactionType }) => {
      return calculateTransactionStatusOptions({ transactionType });
    },
    []
  );

  const financingProgramOptions = useMemo(() => {
    return !_.isNil(financingPrograms)
      ? financingPrograms
      : [{ id: null, name: "" }];
  }, [financingPrograms]);

  const transactionTypeOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const transactionType = event.target.value;

      setShowWarning(
        !_.isNil(previousTransactionType) &&
          previousTransactionType !== transactionType
      );

      formik.setFieldValue("transaction_type", transactionType);

      if (
        transactionType in
        [ETransactionType.EQUITY_TRANSACTION, ETransactionType.DEBT_TRANSACTION]
      ) {
        checkAndUpdateStatusOnTypeChange({
          formik,
          status: EPayableBackTransactionStatus,
        });
      } else if (
        transactionType in
        [
          ETransactionType.TAX_CREDIT_ALLOCATION_TRANSACTION,
          ETransactionType.OTHER_TRANSACTION,
          ETransactionType.OTHER_TRANSACTION,
        ]
      ) {
        checkAndUpdateStatusOnTypeChange({
          formik,
          status: ETransactionStatus,
        });
      }
    },

    [formik, previousTransactionType]
  );

  return (
    <>
      {_.isNil(transactionIdentifier) ? (
        <Stack>
          <DialogTitle
            sx={{
              margin: theme.spacing(2, 2, 0, 2),
              padding: 2,
              borderRadius: theme.spacing(1),
              backgroundColor: colors.blue10,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <AddIcon
                style={{
                  height: "35px",
                  width: "35px",
                }}
              />
              <Text variant="h2">New transaction</Text>
            </Stack>
          </DialogTitle>
          <DialogTitle
            sx={{
              margin: theme.spacing(2, 2, 0, 2),
              padding: 2,
              borderRadius: theme.spacing(1),
              backgroundColor: colors.blue60,
            }}
          >
            <Text variant="h2">General details</Text>
          </DialogTitle>
        </Stack>
      ) : (
        <>
          <DialogTitle
            sx={{
              margin: theme.spacing(2, 2, 0, 2),
              padding: 2,
              borderRadius: theme.spacing(1),
              backgroundColor: colors.blue10,
            }}
          >
            <Text
              variant="text4"
              sx={{
                fontSize: 10,
                fontWeight: 500,
                color: colors.blue100,
                letterSpacing: 0.3,
              }}
            >
              TRANSACTION
            </Text>
            <Text variant="h2">{transactionIdentifier}</Text>
            <Text variant="text4" color={colors.gray60}>
              {lastUpdatedInformation}
            </Text>
          </DialogTitle>
          <DialogTitle
            sx={{
              margin: theme.spacing(2, 2, 0, 2),
              padding: 2,
              borderRadius: theme.spacing(1),
              backgroundColor: colors.blue60,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <EditIcon
                style={{
                  height: "35px",
                  width: "35px",
                }}
              />
              <Text variant="h2">Edit general details</Text>
            </Stack>
          </DialogTitle>
        </>
      )}
      <Stack spacing={3} padding={2} marginTop={2}>
        <TransactionTextField
          fieldName="capital_provider"
          label="Capital provider"
          disabled
          formik={formik}
        />
        <Autocomplete
          renderInput={(params) => (
            <TextField
              {...params}
              label="Financing program"
              InputLabelProps={{ shrink: true }}
            />
          )}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);

            if (params.inputValue !== "") {
              filtered.push({
                name: params?.inputValue,
                id: null,
              });
            }

            return filtered;
          }}
          PaperComponent={AutocompletePaperComponent}
          freeSolo
          autoComplete
          autoHighlight
          value={
            financingProgramOptions.find(
              (option) => option.id === formik.values.financing_program_id
            ) || formik.values.financing_program_name
          }
          options={financingProgramOptions}
          renderOption={(props, option: IFinancingProgram) => (
            <li
              {...props}
              key={
                _.isString(option) ? option : `${option?.name}-${option?.id}`
              }
            >
              {_.isNil(option.id) ? `Create "${option.name}"` : option?.name}
            </li>
          )}
          getOptionLabel={(option) =>
            _.isString(option) ? option : option.name
          }
          onChange={(event, option) => {
            if (!_.isNil(option) && !_.isString(option)) {
              formik.setFieldValue("financing_program_id", option.id);
              formik.setFieldValue("financing_program_name", option.name);
            } else {
              formik.setFieldValue("financing_program_name", null);
              formik.setFieldValue("financing_program_id", null);
            }
          }}
        />
        <Stack display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
          <TransactionSelectField
            formik={formik}
            options={TRANSACTION_TYPE_OPTIONS}
            inputLabel="Transaction type"
            fieldName="transaction_type"
            onChange={transactionTypeOnChange}
            showWarning={showWarning}
          />
          <TransactionTextField
            fieldName="amount"
            label="Amount"
            formik={formik}
            showDollarSign
            isNumber
          />
        </Stack>

        <TransactionSelectField
          formik={formik}
          options={transactionStatusOptions({
            transactionType: formik.values.transaction_type as ETransactionType,
          })}
          inputLabel="Status"
          fieldName="status"
          disabled={formik.values.transaction_type === ""}
        />
        <TransactionTextField
          fieldName="description"
          label="Description"
          formik={formik}
          multiline={true}
          rows={5}
        />
      </Stack>
    </>
  );
};

export default TransactionGeneralInformationForm;
